/* majak 사운드 — Web Audio API로 즉석 합성(에셋 0).
 * 효과음(SFX)과 배경음악(BGM)은 독립 토글. UI 레이어이므로 AudioContext/Math.random 허용. */

const MUTE_KEY = 'majak.muted'; // 효과음 음소거
const MUSIC_KEY = 'majak.music'; // 배경음악 on/off

let ctx: AudioContext | null = null;
let muted = loadBool(MUTE_KEY, false); // 기본 효과음 켜짐
let musicOn = loadBool(MUSIC_KEY, true); // 기본 배경음악 켜짐

function loadBool(key: string, dflt: boolean): boolean {
  try {
    if (typeof localStorage === 'undefined') return dflt;
    const v = localStorage.getItem(key);
    return v === null ? dflt : v === '1';
  } catch {
    return dflt;
  }
}
function saveBool(key: string, v: boolean): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, v ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** AudioContext (필요 시 생성·재개). 음소거와 무관 — 음악도 사용. */
function acRaw(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

/** 효과음용 — 음소거면 null. */
function sfx(): AudioContext | null {
  return muted ? null : acRaw();
}

interface ToneOpts {
  type?: OscillatorType;
  gain?: number;
  glideTo?: number;
  dest?: AudioNode;
}

function tone(c: AudioContext, freq: number, t0: number, dur: number, o: ToneOpts = {}): void {
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(freq, t0);
  if (o.glideTo) osc.frequency.exponentialRampToValueAtTime(o.glideTo, t0 + dur);
  const peak = o.gain ?? 0.15;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(o.dest ?? c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.03);
}

interface NoiseOpts {
  filter?: number;
  q?: number;
  gain?: number;
}

function noise(c: AudioContext, t0: number, dur: number, o: NoiseOpts = {}): void {
  const n = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, n, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = o.filter ?? 1500;
  bp.Q.value = o.q ?? 1;
  const g = c.createGain();
  g.gain.setValueAtTime(o.gain ?? 0.3, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(bp).connect(g).connect(c.destination);
  src.start(t0);
  src.stop(t0 + dur + 0.03);
}

export const sound = {
  clack(): void {
    const c = sfx();
    if (!c) return;
    const t = c.currentTime;
    noise(c, t, 0.07, { filter: 1700, q: 0.8, gain: 0.38 });
    tone(c, 180, t, 0.06, { type: 'triangle', gain: 0.1, glideTo: 90 });
  },
  draw(): void {
    const c = sfx();
    if (!c) return;
    noise(c, c.currentTime, 0.035, { filter: 2700, q: 1, gain: 0.12 });
  },
  riichi(): void {
    const c = sfx();
    if (!c) return;
    const t = c.currentTime;
    tone(c, 659, t, 0.16, { gain: 0.18 });
    tone(c, 988, t + 0.1, 0.3, { gain: 0.18 });
  },
  win(): void {
    const c = sfx();
    if (!c) return;
    const t = c.currentTime;
    [523, 659, 784, 1047].forEach((f, i) => tone(c, f, t + i * 0.09, 0.34, { gain: 0.18 }));
  },
  lose(): void {
    const c = sfx();
    if (!c) return;
    const t = c.currentTime;
    tone(c, 320, t, 0.24, { type: 'sawtooth', gain: 0.12, glideTo: 150 });
    noise(c, t, 0.08, { filter: 700, gain: 0.18 });
  },
  ryuukyoku(): void {
    const c = sfx();
    if (!c) return;
    tone(c, 200, c.currentTime, 0.42, { gain: 0.12, glideTo: 160 });
  },
  money(): void {
    const c = sfx();
    if (!c) return;
    const t = c.currentTime;
    tone(c, 988, t, 0.09, { type: 'square', gain: 0.09 });
    tone(c, 1319, t + 0.07, 0.16, { type: 'square', gain: 0.09 });
  },
  button(): void {
    const c = sfx();
    if (!c) return;
    tone(c, 660, c.currentTime, 0.03, { gain: 0.05 });
  },
};

// ───────────────────────── 배경음악(BGM) ─────────────────────────
// 미니멀 앰비언트 — 지속음 없이, 가끔 부드럽게 울리는 종소리 + 잔잔한 에코.

const SCALE = [392, 440, 523.25, 587.33, 659.25, 783.99]; // G A C D E G (높은 펜타토닉)
let musicGain: GainNode | null = null;
let schedulerTimer: ReturnType<typeof setInterval> | null = null;
let nextNoteTime = 0;

/** 부드러운 종소리(긴 감쇠 + 로우패스). 지속음 아님. */
function bell(c: AudioContext, freq: number, t0: number, gain: number): void {
  if (!musicGain) return;
  const dur = 3.2;
  const o = c.createOscillator();
  const g = c.createGain();
  const lp = c.createBiquadFilter();
  o.type = 'sine';
  o.frequency.value = freq;
  lp.type = 'lowpass';
  lp.frequency.value = 2600;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.04);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(lp).connect(g).connect(musicGain);
  o.start(t0);
  o.stop(t0 + dur + 0.05);
}

function scheduleLoop(): void {
  const c = acRaw();
  if (!c || !musicGain) return;
  while (nextNoteTime < c.currentTime + 0.5) {
    if (Math.random() > 0.34) {
      // ~66% 연주, ~34% 쉼 — 여백이 많음
      const f = SCALE[Math.floor(Math.random() * SCALE.length)];
      bell(c, f, nextNoteTime, 0.03 + Math.random() * 0.015);
      if (Math.random() > 0.5) bell(c, f, nextNoteTime + 0.28, 0.012); // 잔잔한 에코
    }
    nextNoteTime += 1.8 + Math.random() * 1.5; // 1.8~3.3초 간격(느리고 성김)
  }
}

export function startMusic(): void {
  if (!musicOn) return;
  const c = acRaw();
  if (!c || schedulerTimer !== null) return;

  if (!musicGain) {
    musicGain = c.createGain();
    musicGain.gain.value = 0;
    musicGain.connect(c.destination);
  }
  musicGain.gain.cancelScheduledValues(c.currentTime);
  musicGain.gain.setValueAtTime(Math.max(0.0001, musicGain.gain.value), c.currentTime);
  musicGain.gain.linearRampToValueAtTime(0.85, c.currentTime + 2.5); // 부드러운 페이드 인

  nextNoteTime = c.currentTime + 0.6;
  schedulerTimer = setInterval(scheduleLoop, 200);
}

export function stopMusic(): void {
  if (schedulerTimer !== null) {
    clearInterval(schedulerTimer);
    schedulerTimer = null;
  }
  const c = acRaw();
  if (c && musicGain) {
    musicGain.gain.cancelScheduledValues(c.currentTime);
    musicGain.gain.setValueAtTime(musicGain.gain.value, c.currentTime);
    musicGain.gain.linearRampToValueAtTime(0, c.currentTime + 1);
  }
}

// ───────────────────────── 토글 ─────────────────────────

export function isMuted(): boolean {
  return muted;
}
export function setMuted(m: boolean): void {
  muted = m;
  saveBool(MUTE_KEY, m);
}
export function toggleMuted(): boolean {
  setMuted(!muted);
  return muted;
}

export function isMusicOn(): boolean {
  return musicOn;
}
export function setMusicOn(on: boolean): void {
  musicOn = on;
  saveBool(MUSIC_KEY, on);
  if (on) startMusic();
  else stopMusic();
}
export function toggleMusic(): boolean {
  setMusicOn(!musicOn);
  return musicOn;
}
